import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { format } from "date-fns";
import { getNextTides, useTideData } from "@/lib/tides";
import { convertHeight, heightUnit } from "@/lib/units";
import { unitSystemAtom } from "@/atoms/units";
import { useRecoilValue } from "recoil";

interface TideCardProps {
  lat: number;
  lon: number;
  label?: string;
  region?: string | null;
}

export default function TideCard({ lat, lon, label, region }: TideCardProps) {
  const { data: tideData, isLoading, isError } = useTideData(lat, lon, region);
  const units = useRecoilValue(unitSystemAtom);
  const nextTides = getNextTides(tideData?.predictions);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          🌊 Tides{label ? ` — ${label}` : ""}
        </Typography>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {isError && (
          <Typography variant="body2" color="error">
            Unable to load tide data
          </Typography>
        )}

        {nextTides.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {nextTides.map((tide, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                    }}
                  >
                    {tide.type === "H" ? "⬆️" : "⬇️"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {tide.type === "H" ? "High" : "Low"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {convertHeight(tide.height, units).toFixed(1)} {heightUnit(units)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(tide.time, "h:mm a")}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!isLoading && !isError && nextTides.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No upcoming tide data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
